import { useCallback, useReducer, useRef, useEffect, useMemo } from 'react'
import {
  menuOrItemsMatchSearch,
  toDietaryTagKey,
} from '@components/groupOrders/UniversalMenuExplorer/utils'
import {
  VendorGroup,
  PreviewMenu,
  PreviewMenuItem,
  SearchUniversalExplorerMenusReq,
  UniversalMenuExplorerMenu,
  UniversalMenuExplorerFilters,
  DietaryTagKey,
} from '@types'
import {
  computeItemAvailbility,
  computeMenuAvailability,
  decorateItem,
  groupByVendor,
} from '@components/groupOrders/UniversalMenuExplorer/utils'
import { Mode } from './UniversalMenuExplorer'

export interface UseGOMenuSearchProps {
  blacklistedVendorIds: Set<string>
  blacklistedItemIds: Set<string>
  hqId: number
  search: string
  serverSearch: string
  mode: Mode

  page?: number
  limit?: number
  includeNonUniversal: boolean
  searchGOMenus: (params: SearchUniversalExplorerMenusReq) => Promise<any>
  displayFailureMessage: (mssg: string) => void
}

export interface UseGOMenuSearchResult {
  loadingMenus: boolean
  menus: VendorGroup[]
  filters: UniversalMenuExplorerFilters
  resetFilters: () => void
  setEnableSides: (val: boolean) => void
  setEnableModifications: (val: boolean) => void
  setExcludedDietaryTags: (val: string[]) => void
  setFlatPriceMarginMin: (val: number | null) => void
  setSubisdyType: (t: string) => void
  setSubsidyAmt: (n: number | null) => void
  setDayOfWeek: (n: number | null) => void
  setMaxPriceLimit: (n: number | null) => void
  setMaxModPriceLimit: (n: number | null) => void
  setMaxItemTotalPriceLimit: (n: number | null) => void
}

type Act =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_MENUS'; payload: UniversalMenuExplorerMenu[] }
  | { type: 'SET_ENABLE_SIDES'; payload: boolean }
  | { type: 'SET_ENABLE_MODS'; payload: boolean }
  | { type: 'SET_EXCLUDED_DIETARY_TAGS'; payload: string[] }
  | { type: 'SET_FLAT_PRICE_MARGIN_MIN'; payload: number | null }
  | { type: 'SET_SUBSIDY_TYPE'; payload: string | null }
  | { type: 'SET_SUBSIDY_AMT'; payload: number | null }
  | { type: 'SET_DAY_OF_WEEK'; payload: number | null }
  | { type: 'SET_MAX_PRICE_LIMIT'; payload: number | null }
  | { type: 'SET_MAX_MOD_PRICE_LIMIT'; payload: number | null }
  | { type: 'SET_MAX_ITEM_TOTAL_PRICE_LIMIT'; payload: number | null }
  | { type: 'RESET_FILTERS' }

type State = {
  loadingMenus: boolean
  rawMenus: UniversalMenuExplorerMenu[]
  filters: UniversalMenuExplorerFilters
}

const initialFilters: UniversalMenuExplorerFilters = {
  enableSides: true,
  enableMods: true,
  excludedDietaryTagsKeys: [],
  excludedDietaryTags: [],
  flatPriceMarginMin: null,
  subsidyType: null,
  subsidyAmt: null,
  dayOfWeek: null,
  maxPriceLimit: null,
  maxModPriceLimit: null,
  maxTotalItemPriceLimit: null,
}

const initialState: State = {
  loadingMenus: false,
  rawMenus: [],
  filters: initialFilters,
}

const reducer = (s: State, a: Act): State => {
  switch (a.type) {
    case 'SET_LOADING':
      return { ...s, loadingMenus: a.payload }
    case 'SET_MENUS':
      return { ...s, rawMenus: a.payload ?? [] }
    case 'SET_FLAT_PRICE_MARGIN_MIN':
      return { ...s, filters: { ...s.filters, flatPriceMarginMin: a.payload } }
    case 'SET_ENABLE_SIDES':
      return { ...s, filters: { ...s.filters, enableSides: a.payload } }
    case 'SET_ENABLE_MODS':
      return { ...s, filters: { ...s.filters, enableMods: a.payload } }
    case 'SET_EXCLUDED_DIETARY_TAGS': {
      const labels = a.payload
      const keys = labels
        .map(toDietaryTagKey)
        .filter((k): k is DietaryTagKey => Boolean(k))

      return {
        ...s,
        filters: {
          ...s.filters,
          excludedDietaryTags: a.payload,
          excludedDietaryTagsKeys: keys,
        },
      }
    }
    case 'SET_SUBSIDY_TYPE':
      return { ...s, filters: { ...s.filters, subsidyType: a.payload } }
    case 'SET_SUBSIDY_AMT':
      return { ...s, filters: { ...s.filters, subsidyAmt: a.payload } }
    case 'SET_DAY_OF_WEEK':
      return { ...s, filters: { ...s.filters, dayOfWeek: Number(a.payload) } }
    case 'SET_MAX_PRICE_LIMIT':
      return { ...s, filters: { ...s.filters, maxPriceLimit: a.payload } }
    case 'SET_MAX_MOD_PRICE_LIMIT':
      return { ...s, filters: { ...s.filters, maxModPriceLimit: a.payload } }
    case 'SET_MAX_ITEM_TOTAL_PRICE_LIMIT':
      return {
        ...s,
        filters: { ...s.filters, maxTotalItemPriceLimit: a.payload },
      }
    case 'RESET_FILTERS':
      return { ...s, filters: { ...initialFilters } }
    default:
      return s
  }
}

export const useGOMenuSearch = ({
  blacklistedItemIds,
  blacklistedVendorIds,
  hqId,
  search,
  serverSearch,
  mode,

  page = 0,
  limit = 25,
  includeNonUniversal = false,
  searchGOMenus,
  displayFailureMessage,
}: UseGOMenuSearchProps): UseGOMenuSearchResult => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const previewFetchedRef = useRef(false)

  useEffect(() => {
    if (mode !== 'preview' || previewFetchedRef.current) {
      return
    }

    let cancelled = false
    const fetchMenus = async () => {
      if (!hqId) {
        return
      }
      dispatch({ type: 'SET_LOADING', payload: true })
      try {
        const resp = await searchGOMenus({ hqId, includeNonUniversal })
        const next: UniversalMenuExplorerMenu[] = Array.isArray(
          resp?.finalMenus,
        )
          ? resp.finalMenus
          : resp ?? []
        if (!cancelled) {
          dispatch({ type: 'SET_MENUS', payload: next })
          dispatch({ type: 'SET_LOADING', payload: false })
          previewFetchedRef.current = true
        }
      } catch (e: any) {
        if (!cancelled) {
          displayFailureMessage(`Error fetching menus: ${e?.message ?? e}`)
        }
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    }
    fetchMenus()

    return () => {
      cancelled = true
    }
  }, [hqId, mode, includeNonUniversal, searchGOMenus, displayFailureMessage])

  useEffect(() => {
    if (mode !== 'apply') {
      return
    }

    const vendorBlacklistIds = blacklistedVendorIds.size
      ? [...blacklistedVendorIds]
      : undefined
    let cancelled = false
    const fetchMenus = async () => {
      if (!hqId) {
        return
      }
      const req = {
        hqId,
        page,
        limit,
        search: serverSearch,
        blacklistedVendorIds: vendorBlacklistIds,
        includeNonUniversal,
      }
      dispatch({ type: 'SET_LOADING', payload: true })
      try {
        const resp = await searchGOMenus(req)
        const next: UniversalMenuExplorerMenu[] = Array.isArray(
          resp?.finalMenus,
        )
          ? resp.finalMenus
          : resp ?? []
        if (!cancelled) {
          dispatch({ type: 'SET_MENUS', payload: next })
          dispatch({ type: 'SET_LOADING', payload: false })
        }
      } catch (e: any) {
        if (!cancelled) {
          displayFailureMessage(`Error fetching menus: ${e?.message ?? e}`)
        }
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    }
    fetchMenus()

    return () => {
      cancelled = true
    }
  }, [
    hqId,
    includeNonUniversal,
    mode,
    page,
    limit,
    serverSearch,
    blacklistedVendorIds,
    searchGOMenus,
    displayFailureMessage,
  ])

  const setEnableSides = useCallback((val: boolean) => {
    dispatch({ type: 'SET_ENABLE_SIDES', payload: val })
  }, [])
  const setEnableModifications = useCallback((val: boolean) => {
    dispatch({ type: 'SET_ENABLE_MODS', payload: val })
  }, [])
  const setExcludedDietaryTags = useCallback((val: string[]) => {
    dispatch({ type: 'SET_EXCLUDED_DIETARY_TAGS', payload: val })
  }, [])
  const setSubisdyType = useCallback((t: string) => {
    dispatch({ type: 'SET_SUBSIDY_TYPE', payload: t ?? null })
  }, [])
  const setSubsidyAmt = useCallback((n: number | null) => {
    dispatch({ type: 'SET_SUBSIDY_AMT', payload: n })
  }, [])
  const setFlatPriceMarginMin = useCallback((n: number | null) => {
    dispatch({ type: 'SET_FLAT_PRICE_MARGIN_MIN', payload: n })
  }, [])
  const setDayOfWeek = useCallback((n: number | null) => {
    dispatch({ type: 'SET_DAY_OF_WEEK', payload: n })
  }, [])
  const setMaxPriceLimit = useCallback((n: number | null) => {
    dispatch({ type: 'SET_MAX_PRICE_LIMIT', payload: n })
  }, [])
  const setMaxModPriceLimit = useCallback((n: number | null) => {
    dispatch({ type: 'SET_MAX_MOD_PRICE_LIMIT', payload: n })
  }, [])
  const setMaxItemTotalPriceLimit = useCallback((n: number | null) => {
    dispatch({ type: 'SET_MAX_ITEM_TOTAL_PRICE_LIMIT', payload: n })
  }, [])

  const resetFilters = useCallback(() => {
    dispatch({ type: 'RESET_FILTERS' })
  }, [])

  const groupedMenus = useMemo(() => {
    const decoratedMenus: PreviewMenu[] = state.rawMenus.map((m) => {
      const decoratedItems: PreviewMenuItem[] = (m.allMenuItems ?? []).map(
        (it) => {
          const enriched = decorateItem(it, state.filters)
          const availability = computeItemAvailbility(
            enriched,
            blacklistedItemIds,
            state.filters,
          )

          return { ...enriched, availability }
        },
      )

      const availability = computeMenuAvailability(
        m,
        state.filters,
        blacklistedVendorIds,
      )

      return {
        ...m,
        allMenuItems: decoratedItems,
        availability,
      }
    })

    const searched =
      mode === 'apply'
        ? decoratedMenus
        : decoratedMenus.filter((m) =>
            menuOrItemsMatchSearch(
              m.chef.fullName,
              m.name,
              m.allMenuItems.map((i) => ({ name: i.name })),
              search,
            ),
          )

    return groupByVendor(searched)
  }, [
    state.rawMenus,
    state.filters,
    blacklistedVendorIds,
    blacklistedItemIds,
    search,
    mode,
  ])

  return {
    loadingMenus: state.loadingMenus,
    menus: groupedMenus,
    filters: state.filters,

    resetFilters,
    setEnableSides,
    setEnableModifications,
    setExcludedDietaryTags,
    setFlatPriceMarginMin,
    setSubisdyType,
    setSubsidyAmt,
    setDayOfWeek,
    setMaxPriceLimit,
    setMaxModPriceLimit,
    setMaxItemTotalPriceLimit,
  }
}
